import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ChevronDown, ChevronUp, Send } from 'lucide-react';
import { toast } from 'sonner';
import TeacherTicketDialog from '../../components/teacher/TeacherTicketDialog';
import { apiError } from '../../lib/apiError';

const STATUS = {
  open: 'bg-sky-100 text-sky-700 border-sky-300',
  in_progress: 'bg-amber-100 text-amber-700 border-amber-300',
  resolved: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  closed: 'bg-slate-100 text-slate-600 border-slate-300',
};

const TicketThread = ({ ticketId }) => {
  const [ticket, setTicket] = useState(null);
  const [text, setText] = useState('');

  const load = useCallback(() => {
    axios.get(`${API}/tickets/${ticketId}`).then((r) => setTicket(r.data)).catch((e) => toast.error(apiError(e)));
  }, [ticketId]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    try {
      await axios.post(`${API}/tickets/${ticketId}/comments`, { text });
      setText('');
      load();
    } catch (e) {
      toast.error(apiError(e, 'Could not send reply'));
    }
  };

  if (!ticket) return <p className="text-sm text-slate-500 pt-4">Loading...</p>;
  return (
    <div className="pt-4 mt-4 border-t border-slate-100 space-y-3" data-testid={`ticket-thread-${ticketId}`}>
      <p className="text-sm text-slate-700 whitespace-pre-wrap">{ticket.description}</p>
      {ticket.resolution_notes && <p className="text-sm bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-emerald-800">Resolution: {ticket.resolution_notes}</p>}
      {(ticket.comments || []).map((c) => (
        <div key={c.id} className={`text-sm rounded-lg p-3 ${c.type === 'system' ? 'bg-slate-50 text-slate-500 italic' : 'bg-violet-50 text-slate-800'}`}>
          <span className="font-medium not-italic">{c.author_name}: </span>{c.text}
        </div>
      ))}
      <div className="flex gap-2">
        <Textarea rows={2} placeholder="Write a reply..." value={text} onChange={(e) => setText(e.target.value)} data-testid={`ticket-reply-input-${ticketId}`} />
        <Button onClick={send} disabled={!text.trim()} className="bg-violet-600 hover:bg-violet-700 text-white self-end" data-testid={`ticket-reply-send-${ticketId}`} aria-label="Send reply">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const TeacherTickets = () => {
  const [tickets, setTickets] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/tickets`).then((r) => setTickets(r.data || [])).catch((e) => { toast.error(apiError(e)); setTickets([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="teacher-tickets-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-2">Support</h1>
          <p className="text-base md:text-lg text-slate-600">Raise a ticket to the admin team for any issue.</p>
        </div>
        <TeacherTicketDialog onCreated={load} />
      </div>
      {tickets === null ? (
        <p className="text-slate-500">Loading...</p>
      ) : tickets.length === 0 ? (
        <div className="py-14 text-center rounded-2xl border border-dashed border-slate-300 bg-white text-slate-500" data-testid="teacher-no-tickets">No tickets yet.</div>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => (
            <Card key={t.id} className="border-0 shadow" data-testid={`teacher-ticket-${t.id}`}>
              <CardContent className="p-5">
                <button className="w-full flex items-center justify-between gap-3 text-left" onClick={() => setOpenId(openId === t.id ? null : t.id)} data-testid={`teacher-ticket-toggle-${t.id}`}>
                  <div className="min-w-0">
                    <p className="text-xs text-slate-400">{t.ticket_number} · {new Date(t.created_at).toLocaleDateString()}</p>
                    <p className="font-semibold text-slate-900 truncate">{t.title}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={`capitalize ${STATUS[t.status] || ''}`} data-testid={`teacher-ticket-status-${t.id}`}>{t.status.replace('_', ' ')}</Badge>
                    {openId === t.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </button>
                {openId === t.id && <TicketThread ticketId={t.id} />}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeacherTickets;
